import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { useMutation, useQuery } from '@/utils/api';

export type AIBudgetCategory =
  | 'hunts'
  | 'marcus'
  | 'constantine'
  | 'cato'
  | 'h1-triage';

export interface AIBudgetStatus {
  set: boolean;
  max_limit: number;
  current_usage: number;
  reset_duration: string;
  calendar_aligned: boolean;
  last_reset?: string;
  categories?: Partial<Record<AIBudgetCategory, AIBudgetStatus>>;
}

export interface AIBudgetCategoryConfig {
  key: AIBudgetCategory;
  label: string;
}

export const AI_BUDGET_CATEGORIES: AIBudgetCategoryConfig[] = [
  { key: 'hunts', label: 'Hannibal' },
  { key: 'marcus', label: 'Marcus' },
  { key: 'constantine', label: 'Constantine' },
  { key: 'cato', label: 'Cato' },
  { key: 'h1-triage', label: 'H1 Triage' },
];

interface AIBudgetOptions {
  email?: string;
}

/**
 * Reads the per-tenant Bifrost spend cap and current usage. Returns
 * `null` when the tenant has been provisioned but no Customer Budget
 * has been configured yet (backend returns 404 with a discriminated
 * message; we differentiate it from a hard error here).
 */
export const useAIBudgetStatus = ({ email }: AIBudgetOptions = {}) => {
  const axios = useAxios({
    email,
    doNotImpersonate: !!email,
  });

  const queryKey = useGetUserKey(getQueryKey.getAIBudget(), !!email, email);

  return useQuery<AIBudgetStatus | null>({
    queryKey,
    queryFn: async () => {
      try {
        const { data } = await axios.get<AIBudgetStatus>('/bifrost/budget');
        return data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    gcTime: 300_000,
    defaultErrorMessage: 'Failed to load AI spend',
  });
};

/**
 * Budget-provisioning gate for the customer-facing Retest button (F2).
 *
 * Wraps {@link useAIBudgetStatus} and collapses its result into a single
 * `provisioned` boolean: the tenant is considered provisioned only when the
 * `GET /bifrost/budget` call returns 200 with `set === true`. It is NOT
 * provisioned when the call 404s (handler returns 404 only for unprovisioned
 * tenants — bifrost/budget.go:31-39, surfaced as `null` here) OR returns 200
 * with `set === false`. While the query is loading or errors, `provisioned`
 * stays `false` so the Retest button fails closed (disabled with a
 * provisioning prompt) rather than firing a request the backend will 402.
 */
export const useBifrostBudget = () => {
  const { data, status, ...rest } = useAIBudgetStatus();

  // data === null -> 404 (unprovisioned); data.set === false -> provisioned-but-no-budget.
  const provisioned = status === 'success' && data != null && data.set === true;

  return { ...rest, data, status, provisioned };
};

/**
 * Praetorian-only mutation that sets the per-tenant monthly spend cap.
 * Invalidates `useAIBudgetStatus` on success so the card re-renders
 * with the new max_limit (Bifrost echoes back current_usage unchanged).
 */
export const useSetAIBudget = ({ email }: AIBudgetOptions = {}) => {
  const axios = useAxios({
    email,
    doNotImpersonate: !!email,
  });

  const { invalidate } = useAIBudgetStatus({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to update AI spend limit',
    getErrorFromResponse: true,
    mutationFn: async ({ maxLimit }: { maxLimit: number }) => {
      await axios.put('/bifrost/budget', { max_limit: maxLimit });
      invalidate();
      toast.success('AI spend limit updated');
    },
  });
};

export const useSetCategoryBudgets = ({ email }: AIBudgetOptions = {}) => {
  const axios = useAxios({
    email,
    doNotImpersonate: !!email,
  });

  const { invalidate } = useAIBudgetStatus({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to update category allocation',
    getErrorFromResponse: true,
    mutationFn: async (allocations: Record<AIBudgetCategory, number>) => {
      await axios.put('/bifrost/budget/categories', allocations);
      invalidate();
    },
  });
};

export const useDeleteCategoryBudget = ({ email }: AIBudgetOptions = {}) => {
  const axios = useAxios({
    email,
    doNotImpersonate: !!email,
  });

  const { invalidate } = useAIBudgetStatus({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to remove category budget',
    getErrorFromResponse: true,
    mutationFn: async ({ category }: { category: AIBudgetCategory }) => {
      await axios.delete(`/bifrost/budget/category/${category}`);
      invalidate();
    },
  });
};
